import { useCallback } from 'react';
import { FormatNumberOptions, useIntl } from 'react-intl';

export type Unit = 'liter' | 'milliliter' | 'kilogram' | 'gram' | 'milligram';

type PricePreviewOptions =
  | { baseUnit: 'liter'; volumeInMl: number }
  | { baseUnit: '100_milliliter'; volumeInMl: number }
  | { baseUnit: 'kilogram'; weightInGrams: number; isSoldByWeight?: boolean }
  | { baseUnit: '100_gram'; weightInGrams: number }
  | { baseUnit: 'pc'; numberOfUnits: number };

export const useLocaleUtils = () => {
  const intl = useIntl();

  const getCurrencySymbol = useCallback(
    (currencyCode: string) => {
      try {
        const parts: { type: string; value: string }[] = intl.formatNumberToParts(0, {
          style: 'currency',
          currency: currencyCode,
        });
        return parts.find(p => p.type === 'currency')?.value || currencyCode;
      } catch (error) {
        return currencyCode;
      }
    },
    [intl],
  );

  const getLocalizedUnit = useCallback(
    (unit: Unit) => {
      const parts: { type: string; value: string }[] = intl.formatNumberToParts(0, { style: 'unit', unit });
      return parts.find(p => p.type === 'unit')?.value || unit;
    },
    [intl],
  );

  const formatVolumeQuantity = useCallback(
    (volumeInMl: number) =>
      volumeInMl < 1000
        ? intl.formatNumber(volumeInMl, { style: 'unit', unit: 'milliliter' })
        : intl.formatNumber(volumeInMl / 1000, { style: 'unit', unit: 'liter' }),
    [intl],
  );

  const formatWeightQuantity = useCallback(
    (weightInGrams: number, opts?: FormatNumberOptions) =>
      weightInGrams < 1000
        ? intl.formatNumber(weightInGrams, { style: 'unit', unit: 'gram', ...opts })
        : intl.formatNumber(weightInGrams / 1000, { style: 'unit', unit: 'kilogram', ...opts }),
    [intl],
  );

  const priceFormatterPerUnit = useCallback(
    (price: number, currency: string, options: PricePreviewOptions) => {
      if (options.baseUnit === 'liter') {
        const volume = formatVolumeQuantity(options.volumeInMl);
        const pricePerUnit = intl.formatNumber(price / (options.volumeInMl / 1000), { currency, style: 'currency' });
        const unit = getLocalizedUnit('liter');
        return `${intl.formatMessage({
          id: 'menu-item.display-unit-preview-price',
        })} ${volume}, ${pricePerUnit}/${unit}`;
      }

      if (options.baseUnit === '100_milliliter') {
        const volume = formatVolumeQuantity(options.volumeInMl);
        const pricePerUnit = intl.formatNumber(price / (options.volumeInMl / 100), { currency, style: 'currency' });
        const unit = getLocalizedUnit('milliliter');
        return `${intl.formatMessage({
          id: 'menu-item.display-unit-preview-price',
        })} ${volume}, ${pricePerUnit}/${intl.formatNumber(100)} ${unit}`;
      }

      if (options.baseUnit === 'kilogram') {
        const weight = formatWeightQuantity(options.weightInGrams);
        const pricePerUnit = intl.formatNumber(price / (options.weightInGrams / 1000), { currency, style: 'currency' });
        const unit = getLocalizedUnit('kilogram');
        return `${intl.formatMessage({
          id: 'menu-item.display-unit-preview-price',
        })} ${options.isSoldByWeight ? '' : `${weight},`} ${pricePerUnit}/${unit}`;
      }

      if (options.baseUnit === '100_gram') {
        const weight = formatWeightQuantity(options.weightInGrams);
        const pricePerUnit = intl.formatNumber(price / (options.weightInGrams / 100), { currency, style: 'currency' });
        const unit = getLocalizedUnit('gram');
        return `${intl.formatMessage({
          id: 'menu-item.display-unit-preview-price',
        })} ${weight}, ${pricePerUnit}/${intl.formatNumber(100)} ${unit}`;
      }

      const currentPieces = `${options.numberOfUnits} ${intl.formatMessage({ id: 'menu-item.display-unit-pcs' })}`;
      const pricePerUnit = intl.formatNumber(price / options.numberOfUnits, { currency, style: 'currency' });
      const unit = intl.formatMessage({ id: 'menu-item.display-unit-per-pcs' });
      return `${intl.formatMessage({
        id: 'menu-item.display-unit-preview-price',
      })} ${currentPieces}, ${pricePerUnit}/${unit}`;
    },
    [getLocalizedUnit, formatVolumeQuantity, formatWeightQuantity, intl],
  );

  return { getCurrencySymbol, getLocalizedUnit, priceFormatterPerUnit, formatWeightQuantity };
};
